import React, { useState, useCallback, useEffect } from 'react';
import { Node, Connection } from '../types';
import { NODE_CONFIGS } from '../constants';
import { validateWorkflow, testWorkflow } from '../api';
import { Icons } from './NodeComponents';

interface TestModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: Node[];
  connections: Connection[];
  selectedNodeId: string | null;
}

export const TestModal: React.FC<TestModalProps> = ({ 
  isOpen, 
  onClose, 
  nodes, 
  connections, 
  selectedNodeId 
}) => {
  const [activeTab, setActiveTab] = useState<'selected' | 'workflow'>('selected');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<{ isValid: boolean; errors: string[]; warnings: string[] } | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  
  // File attachment state
  const [attachedFile, setAttachedFile] = useState<{ name: string; type: string; base64: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Allowed file types
  const ALLOWED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
    'application/msword', // doc
    'text/plain',
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/mp3',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp'
  ];
  
  const ALLOWED_EXTENSIONS = '.pdf,.docx,.doc,.txt,.mp3,.wav,.ogg,.png,.jpg,.jpeg,.gif,.webp';

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError(null);
    
    if (!file) {
      setAttachedFile(null);
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setFileError('File size must be less than 10MB');
      setAttachedFile(null);
      return;
    }

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(pdf|docx?|txt|mp3|wav|ogg|png|jpe?g|gif|webp)$/i)) {
      setFileError('Unsupported file type. Allowed: PDF, DOCX, TXT, audio, images');
      setAttachedFile(null);
      return;
    }

    // Convert to base64
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix to get pure base64
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setAttachedFile({
        name: file.name,
        type: file.type,
        base64
      });
    } catch (err) {
      setFileError('Failed to read file');
      setAttachedFile(null);
    }
  };

  // Clear attached file
  const clearAttachedFile = () => {
    setAttachedFile(null);
    setFileError(null);
    // Reset file input
    const fileInput = document.getElementById('test-file-input') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  // Reset output when modal opens
  useEffect(() => {
    if (isOpen) {
      // Don't reset input to preserve user's text
      setOutput('');
      setLogs([]);
    }
  }, [isOpen]);

  // Validate workflow when switching to workflow tab
  const handleTabChange = useCallback((tab: 'selected' | 'workflow') => {
    setActiveTab(tab);
    if (tab === 'workflow') {
      const result = validateWorkflow(nodes, connections);
      setValidationResult(result);
    }
  }, [nodes, connections]);

  // Helper to get sorted nodes for workflow simulation
  const getSortedNodesForTest = useCallback(() => {
    const adj: Record<string, string[]> = {};
    const inDegree: Record<string, number> = {};
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    nodes.forEach(n => {
      adj[n.id] = [];
      inDegree[n.id] = 0;
    });

    connections.forEach(c => {
      if (adj[c.source]) adj[c.source].push(c.target);
      if (typeof inDegree[c.target] === 'number') inDegree[c.target]++;
    });

    const queue: string[] = nodes
      .filter(n => inDegree[n.id] === 0)
      .sort((a, b) => {
        if (a.type === 'start') return -1;
        if (b.type === 'start') return 1;
        return 0;
      })
      .map(n => n.id);

    const sortedIds: string[] = [];
    while (queue.length > 0) {
      const u = queue.shift()!;
      sortedIds.push(u);
      if (adj[u]) {
        adj[u].forEach(v => {
          inDegree[v]--;
          if (inDegree[v] === 0) queue.push(v);
        });
      }
    }

    return sortedIds.map(id => nodeMap.get(id)!).filter(Boolean);
  }, [nodes, connections]);

  const runTest = useCallback(async () => {
    setIsLoading(true);
    setOutput('');

    try {
      if (activeTab === 'selected') {
        // Test selected node
        if (!selectedNodeId) {
          setOutput('Error: No node selected. Please select a node to test.');
          return;
        }

        const selectedNode = nodes.find(n => n.id === selectedNodeId);
        if (!selectedNode) {
          setOutput('Error: Selected node not found.');
          return;
        }

        // For agent_engine nodes, test with the workflow configuration
        if (selectedNode.type === 'agent_engine') {
          try {
            const result = (await testWorkflow(nodes, connections, input, attachedFile)) as any;
            if (result.success) {
              if (result.logs) {
                setLogs(result.logs);
              } else {
                setLogs([]);
              }
              // First check for direct response field (new format)
              if (result.response && typeof result.response === 'string') {
                setOutput(result.response);
              } else if (result.results) {
                // Check if we got actual response in results
                const agentResult = result.results.find((r: any) => r.output);
                if (agentResult?.output) {
                  // Extract text from ADK response format
                  const outputData = agentResult.output;
                  let responseText = '';
                  if (Array.isArray(outputData)) {
                    for (const event of outputData) {
                      if (event.content?.parts) {
                        for (const part of event.content.parts) {
                          if (part.text) responseText += part.text;
                        }
                      }
                    }
                  } else if (typeof outputData === 'string') {
                    responseText = outputData;
                  } else {
                    responseText = JSON.stringify(outputData, null, 2);
                  }
                  setOutput(`✅ Agent Response:\n\n${responseText}`);
                } else if (result.note) {
                  setOutput(`⚠️ ${result.note}`);
                } else {
                  setOutput(`✅ Workflow executed successfully.\n\n${JSON.stringify(result.results, null, 2)}`);
                }
              } else {
                setOutput(`✅ Workflow executed successfully.`);
              }
            } else {
              setOutput(`⚠️ Agent not yet deployed.\n\nNode: ${selectedNode.data.agentName || selectedNode.type}\nInput: "${input}"\n\nSave the workflow to deploy and test with real execution.`);
            }
          } catch (error: any) {
            setOutput(`⚠️ Agent not yet deployed.\n\nNode: ${selectedNode.data.agentName || selectedNode.type}\nInput: "${input}"\n\nSave the workflow to deploy and test with real execution.`);
          }
        } else if (selectedNode.type === 'llm') {
          // Show model configuration summary
          const config = selectedNode.data;
          setOutput(`📋 Model Configuration:\n\nProvider: ${config.modelProvider || 'Not set'}\nModel: ${config.modelName || 'Not set'}\nAuth: ${config.authType || 'Not set'}\nTemperature: ${config.temperature ?? 1}\nTop P: ${config.topP ?? 0.95}\nMax Tokens: ${config.maxOutputTokens || 'Default'}\n\n✅ Configuration valid. Connect to Agent Engine to test.`);
        } else if (selectedNode.type === 'mcp') {
          // Show selected tools
          const tools = selectedNode.data.selectedMcpTools || [];
          setOutput(`🔧 MCP Tools Configuration:\n\nServer: ${selectedNode.data.mcpServerUrl || 'Default'}\nSelected Tools (${tools.length}):\n${tools.map((t: string) => `  • ${t}`).join('\n') || '  None selected'}\n\n✅ Configuration valid. Connect to Agent Engine to use tools.`);
        } else if (selectedNode.type === 'memory') {
          // Show memory configuration
          setOutput(`💾 Memory Configuration:\n\nType: ${selectedNode.data.memoryType || 'Not set'}\nEnabled: ${selectedNode.data.memoryEnabled ? 'Yes' : 'No'}\nWindow Size: ${selectedNode.data.windowSize || 'Default'}\n\n✅ Configuration valid. Connect to Agent Engine to enable memory.`);
        } else {
          setOutput(`ℹ️ Node: ${NODE_CONFIGS[selectedNode.type]?.title || selectedNode.type}\n\nThis node type doesn't require testing. It's part of the workflow structure.`);
        }
      } else {
        // Test whole workflow
        const validation = validateWorkflow(nodes, connections);
        if (!validation.isValid) {
          setOutput(`❌ Workflow Validation Failed:\n\n${validation.errors.join('\n')}`);
          return;
        }

        try {
          const result = (await testWorkflow(nodes, connections, input, attachedFile)) as any;
          
          // Check for errors first
          if (!result.success && result.error) {
            setOutput(`❌ Workflow Test Failed:\n\n${result.error}`);
            setLogs([]);
            return;
          }
          
          if (result.logs) {
            setLogs(result.logs);
          } else {
            setLogs([]);
          }
          
          // Check for direct response (preferred path)
          if (result.response) {
            setOutput(result.response);
            return;
          }
          
          if (result.success && result.results) {
            const agentResult = result.results.find((r: any) => r.output);
            if (agentResult?.output) {
              let responseText = '';
              const outputData = agentResult.output;
              if (Array.isArray(outputData)) {
                for (const event of outputData) {
                  if (event.content?.parts) {
                    for (const part of event.content.parts) {
                      if (part.text) responseText += part.text;
                    }
                  }
                }
              } else if (typeof outputData === 'string') {
                responseText = outputData;
              } else {
                responseText = JSON.stringify(outputData, null, 2);
              }
              setOutput(`✅ Workflow Test Successful:\n\n${responseText}`);
            } else if (result.note) {
              // Show note if present (e.g., "Agent not yet saved")
              setOutput(`⚠️ ${result.note}`);
            } else {
              // Show simulation steps only as last resort
              const sortedNodes = getSortedNodesForTest();
              let simulatedOutput = '📋 Workflow Execution Preview:\n\n';
              sortedNodes.forEach((node, index) => {
                simulatedOutput += `Step ${index + 1}: ${NODE_CONFIGS[node.type].title}`;
                if (node.data.agentName) simulatedOutput += ` (${node.data.agentName})`;
                simulatedOutput += '\n';
              });
              simulatedOutput += `\n✅ All ${sortedNodes.length} steps configured correctly.\n\nSave workflow to test with real agent execution.`;
              setOutput(simulatedOutput);
            }
          } else {
            throw new Error(result.error || 'No results from workflow test');
          }
        } catch (error: any) {
          setOutput(`❌ Workflow Test Error:\n\n${error.message || 'Unknown error occurred'}`);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, selectedNodeId, nodes, connections, input, attachedFile, getSortedNodesForTest]);

  if (!isOpen) return null;

  const selectedNode = selectedNodeId ? nodes.find(n => n.id === selectedNodeId) : null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ animation: 'fadeIn 0.15s ease-out' }}
    >
      <div 
        className="bg-white w-[520px] rounded-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'modalSlideIn 0.2s ease-out' }}
      >
        <div className="h-12 border-b border-slate-100 flex items-center justify-between px-4 bg-slate-50 flex-shrink-0">
          <h3 className="text-sm font-bold text-slate-700">Test Execution</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-red-500 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-1 bg-slate-100 flex gap-1 mx-4 mt-4 rounded-lg flex-shrink-0">
          <button 
            onClick={() => handleTabChange('selected')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${activeTab === 'selected' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Test Selected Node
          </button>
          <button 
            onClick={() => handleTabChange('workflow')}
            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${activeTab === 'workflow' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Test Whole Workflow
          </button>
        </div>

        {/* Validation warnings for workflow tab */}
        {activeTab === 'workflow' && validationResult && (
          <div className="px-4 pt-3 flex-shrink-0">
            {!validationResult.isValid && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 mb-2">
                <strong>Errors:</strong>
                <ul className="list-disc list-inside mt-1">
                  {validationResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
            {validationResult.warnings.length > 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                <strong>Warnings:</strong>
                <ul className="list-disc list-inside mt-1">
                  {validationResult.warnings.map((warn, i) => <li key={i}>{warn}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Selected node info */}
        {activeTab === 'selected' && (
          <div className="px-4 pt-3 flex-shrink-0">
            {selectedNode ? (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                Testing: <strong>{selectedNode.data.agentName || NODE_CONFIGS[selectedNode.type]?.title || selectedNode.type}</strong>
              </div>
            ) : (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                ⚠️ No node selected. Select a node on the canvas to test it.
              </div>
            )}
          </div>
        )}

        <div className="px-4 py-4 space-y-4 flex-1 overflow-hidden flex flex-col">
          <div className="flex-shrink-0">
            <label className="block text-[10px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide">Input</label>
            <textarea 
              className="w-full text-xs px-3 py-2 border border-slate-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors bg-white h-20 resize-none"
              placeholder={activeTab === 'selected' ? "Enter input for this node..." : "Enter initial workflow trigger data..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>

          {/* File Attachment Section */}
          <div className="flex-shrink-0">
            <label className="block text-[10px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide">
              Attach File (Optional)
            </label>
            <div className="flex items-center gap-2">
              <label className="flex-1 flex items-center justify-center px-3 py-2 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <input
                  id="test-file-input"
                  type="file"
                  accept={ALLOWED_EXTENSIONS}
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <span className="text-xs text-slate-500">
                  {attachedFile ? (
                    <span className="flex items-center gap-2 text-blue-600">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {attachedFile.name}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Click to attach PDF, DOCX, text, audio, or image
                    </span>
                  )}
                </span>
              </label>
              {attachedFile && (
                <button
                  onClick={clearAttachedFile}
                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Remove file"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {fileError && (
              <p className="text-[10px] text-red-500 mt-1">{fileError}</p>
            )}
            {attachedFile && (
              <p className="text-[9px] text-slate-400 mt-1">
                Type: {attachedFile.type || 'unknown'} • Size: {Math.round(attachedFile.base64.length * 0.75 / 1024)}KB
              </p>
            )}
          </div>
          
          <div className="flex-1 min-h-0 flex flex-col">
            <label className="block text-[10px] font-bold text-slate-500 mb-1.5 uppercase tracking-wide flex-shrink-0">Output & Execution Logs</label>
            <div className="flex-1 min-h-[150px] max-h-[220px] bg-[#0d1117] border border-slate-700/60 rounded-lg overflow-hidden flex flex-col shadow-inner backdrop-blur-md">
              {/* Terminal Title Bar */}
              <div className="h-7 bg-[#161b22] border-b border-slate-800 px-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 hover:bg-red-400 transition-colors"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 hover:bg-yellow-400 transition-colors"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500 hover:bg-green-400 transition-colors"></span>
                  <span className="text-[10px] text-slate-400 font-medium ml-3 font-mono tracking-wide">system@engazeiq_orchestrator:~</span>
                </div>
                <div className="text-[9px] text-slate-500 font-mono">bash</div>
              </div>
              {/* Terminal Logs View */}
              <div className="flex-1 p-3.5 overflow-y-auto font-mono text-[12px] text-slate-300 space-y-3 custom-scrollbar">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
                    <div className="flex gap-2">
                      <span className="w-2 h-2 bg-blue-500/80 rounded-full animate-ping"></span>
                      <span className="w-2 h-2 bg-purple-500/80 rounded-full animate-ping" style={{animationDelay: '0.15s'}}></span>
                      <span className="w-2 h-2 bg-pink-500/80 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></span>
                    </div>
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest animate-pulse">Running Execution...</span>
                  </div>
                ) : logs && logs.length > 0 ? (
                  <div className="space-y-3">
                    {logs.map((log, index) => {
                      const isUser = log.agent === 'user' || log.role === 'user';
                      const labelColor = isUser ? 'text-blue-400' : 'text-fuchsia-400';
                      const bgClass = isUser ? 'bg-blue-950/10 border-blue-900/30' : 'bg-fuchsia-950/10 border-fuchsia-900/30';
                      return (
                        <div key={index} className={`p-2.5 rounded border ${bgClass} leading-relaxed transition-all duration-300 hover:bg-opacity-20`} style={{ animation: 'fadeIn 0.3s ease-out' }}>
                          <div className="flex items-center gap-2 mb-1 opacity-90">
                            <span className={`${labelColor} font-bold text-[11px] uppercase tracking-wider`}>{isUser ? 'USER' : log.agent}</span>
                            <span className="text-slate-600 text-[10px]">❯</span>
                          </div>
                          <div className="text-slate-200 whitespace-pre-wrap pl-1 font-mono">{log.text}</div>
                        </div>
                      );
                    })}
                    {output && (
                      <div className="p-3 rounded bg-emerald-950/20 border border-emerald-900/40 text-emerald-300 leading-relaxed shadow-sm mt-4" style={{ animation: 'fadeIn 0.4s ease-out' }}>
                        <div className="flex items-center gap-2 mb-1.5 opacity-90">
                          <span className="font-bold text-emerald-400 text-[11px] uppercase tracking-wider">SYSTEM</span>
                          <span className="text-slate-600 text-[10px]">❯</span>
                          <span className="font-medium text-[10px] text-emerald-500/80 uppercase">Execution Complete</span>
                        </div>
                        <div className="text-emerald-100 whitespace-pre-wrap font-mono pl-1 text-[13px]">{output}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-slate-500/70 font-mono whitespace-pre-wrap break-words leading-relaxed flex items-center h-full">
                    <span className="text-green-500/50 mr-2">❯</span> {output || 'Ready to run test...'}
                    {!output && <span className="inline-block w-1.5 h-4 ml-1 bg-green-500/50 animate-pulse"></span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button 
            onClick={runTest}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            disabled={isLoading || (activeTab === 'workflow' && validationResult !== null && !validationResult.isValid)}
          >
            <Icons.Flask />
            Run Test
          </button>
        </div>
      </div>
    </div>
  );
};
